import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';

import { ApiError, apiRequest } from '../lib/api-client';

export type ResourceKey = 'outlets' | 'categories' | 'products' | 'suppliers' | 'customers';

type RecordValue = string | number | boolean | null | { id: string; name: string };
type MasterRecord = { id: string; [key: string]: RecordValue };
type Field = {
  name: string;
  label: string;
  type?: 'text' | 'email' | 'number' | 'url' | 'textarea' | 'checkbox' | 'date' | 'category';
  required?: boolean;
};
type ResourceConfig = {
  title: string;
  singular: string;
  description: string;
  columns: Array<{ key: string; label: string }>;
  fields: Field[];
};

const configs: Record<ResourceKey, ResourceConfig> = {
  outlets: {
    title: 'Outlets',
    singular: 'Outlet',
    description: 'Lokasi operasional Ajisai House.',
    columns: [
      { key: 'code', label: 'Kode' },
      { key: 'name', label: 'Nama outlet' },
      { key: 'phone', label: 'Telepon' },
      { key: 'openingDate', label: 'Tanggal buka' },
      { key: 'address', label: 'Alamat' },
      { key: 'isActive', label: 'Status' },
    ],
    fields: [
      { name: 'code', label: 'Kode outlet', required: true },
      { name: 'name', label: 'Nama outlet', required: true },
      { name: 'phone', label: 'Telepon' },
      { name: 'openingDate', label: 'Tanggal buka', type: 'date' },
      { name: 'address', label: 'Alamat', type: 'textarea', required: true },
      { name: 'isActive', label: 'Aktif', type: 'checkbox' },
    ],
  },
  categories: {
    title: 'Kategori Produk',
    singular: 'Kategori',
    description: 'Kelompok produk untuk katalog yang konsisten.',
    columns: [
      { key: 'code', label: 'Kode' },
      { key: 'name', label: 'Nama kategori' },
      { key: 'isActive', label: 'Status' },
    ],
    fields: [
      { name: 'code', label: 'Kode kategori', required: true },
      { name: 'name', label: 'Nama kategori', required: true },
      { name: 'isActive', label: 'Aktif', type: 'checkbox' },
    ],
  },
  products: {
    title: 'Produk',
    singular: 'Produk',
    description: 'Master katalog dan harga produk.',
    columns: [
      { key: 'sku', label: 'SKU' },
      { key: 'name', label: 'Produk' },
      { key: 'category', label: 'Kategori' },
      { key: 'sellingPrice', label: 'Harga jual' },
      { key: 'isActive', label: 'Status' },
    ],
    fields: [
      { name: 'name', label: 'Nama produk', required: true },
      { name: 'description', label: 'Deskripsi', type: 'textarea' },
      { name: 'categoryId', label: 'Kategori', type: 'category', required: true },
      { name: 'costPrice', label: 'Harga modal', type: 'number', required: true },
      { name: 'sellingPrice', label: 'Harga jual', type: 'number', required: true },
      { name: 'imageUrl', label: 'URL gambar produk', type: 'url' },
      { name: 'isActive', label: 'Aktif', type: 'checkbox' },
    ],
  },
  suppliers: {
    title: 'Suppliers',
    singular: 'Supplier',
    description: 'Mitra pemasok bunga dan kebutuhan toko.',
    columns: [
      { key: 'code', label: 'Kode' },
      { key: 'name', label: 'Supplier' },
      { key: 'contactName', label: 'Kontak' },
      { key: 'phone', label: 'Telepon' },
      { key: 'isActive', label: 'Status' },
    ],
    fields: [
      { name: 'code', label: 'Kode supplier', required: true },
      { name: 'name', label: 'Nama supplier', required: true },
      { name: 'contactName', label: 'Nama kontak' },
      { name: 'phone', label: 'Telepon' },
      { name: 'email', label: 'Email', type: 'email' },
      { name: 'address', label: 'Alamat', type: 'textarea' },
      { name: 'notes', label: 'Catatan', type: 'textarea' },
      { name: 'isActive', label: 'Aktif', type: 'checkbox' },
    ],
  },
  customers: {
    title: 'Customers',
    singular: 'Customer',
    description: 'Profil pelanggan untuk pelayanan yang lebih personal.',
    columns: [
      { key: 'code', label: 'ID' },
      { key: 'name', label: 'Nama' },
      { key: 'phone', label: 'WhatsApp' },
      { key: 'email', label: 'Email' },
      { key: 'isActive', label: 'Status' },
    ],
    fields: [
      { name: 'name', label: 'Nama pelanggan', required: true },
      { name: 'phone', label: 'Nomor WhatsApp' },
      { name: 'email', label: 'Email', type: 'email' },
      { name: 'address', label: 'Alamat', type: 'textarea' },
      { name: 'instagram', label: 'Instagram' },
      { name: 'birthDate', label: 'Tanggal lahir', type: 'date' },
      { name: 'notes', label: 'Catatan', type: 'textarea' },
      { name: 'isActive', label: 'Aktif', type: 'checkbox' },
    ],
  },
};

const money = new Intl.NumberFormat('id-ID', {
  style: 'currency',
  currency: 'IDR',
  maximumFractionDigits: 0,
});

function scalar(value: RecordValue | undefined): string {
  return typeof value === 'string' || typeof value === 'number' ? String(value) : '';
}

function displayValue(record: MasterRecord, key: string) {
  const value = record[key];
  if (key === 'isActive')
    return (
      <span
        className={`rounded-full px-2.5 py-1 text-xs font-bold ${
          value ? 'bg-green-50 text-green-700' : 'bg-slate-100 text-slate-600'
        }`}
      >
        {value ? 'Aktif' : 'Nonaktif'}
      </span>
    );
  if (key === 'sellingPrice') return money.format(Number(value));
  if (key === 'openingDate' && typeof value === 'string') {
    return new Intl.DateTimeFormat('id-ID', { dateStyle: 'medium' }).format(new Date(value));
  }
  if (value && typeof value === 'object') return value.name;
  return String(value ?? '—');
}

export function MasterData({ resource, canWrite }: { resource: ResourceKey; canWrite: boolean }) {
  const config = configs[resource];
  const [records, setRecords] = useState<MasterRecord[]>([]);
  const [categories, setCategories] = useState<MasterRecord[]>([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [editing, setEditing] = useState<MasterRecord | null | undefined>(undefined);
  const [form, setForm] = useState<Record<string, string | boolean>>({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const limit = 10;

  async function load() {
    try {
      const result = await apiRequest<{
        data: MasterRecord[];
        meta: { total: number };
      }>(`/${resource}?search=${encodeURIComponent(search)}&page=${page}&limit=${limit}`);
      setRecords(result.data);
      setTotal(result.meta.total);
      setError('');
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : 'Data gagal dimuat.');
    }
  }

  useEffect(() => {
    void load();
  }, [resource, page]);

  useEffect(() => {
    if (resource === 'products') {
      apiRequest<{ data: MasterRecord[] }>('/categories?limit=100')
        .then((result) => setCategories(result.data))
        .catch(() => setCategories([]));
    }
  }, [resource]);

  function openForm(record: MasterRecord | null) {
    const initial: Record<string, string | boolean> = {};
    for (const field of config.fields) {
      const value =
        field.name === 'categoryId' && record?.category
          ? (record.category as { id: string }).id
          : record?.[field.name];
      initial[field.name] =
        field.type === 'checkbox' ? (value === undefined ? true : Boolean(value)) : scalar(value);
    }
    setForm(initial);
    setEditing(record);
    setError('');
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError('');
    const payload = Object.fromEntries(
      config.fields
        .filter((field) => field.required || field.type === 'checkbox' || form[field.name] !== '')
        .map((field) => [
          field.name,
          field.type === 'number' ? Number(form[field.name]) : form[field.name],
        ]),
    );
    try {
      await apiRequest(`/${resource}${editing ? `/${editing.id}` : ''}`, {
        method: editing ? 'PATCH' : 'POST',
        body: JSON.stringify(payload),
      });
      setEditing(undefined);
      await load();
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : 'Data gagal disimpan.');
    } finally {
      setBusy(false);
    }
  }

  async function remove(record: MasterRecord) {
    if (!window.confirm(`Nonaktifkan dan hapus ${config.singular.toLowerCase()} ini?`)) return;
    try {
      await apiRequest(`/${resource}/${record.id}`, { method: 'DELETE' });
      await load();
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : 'Data gagal dihapus.');
    }
  }

  return (
    <section className="page-shell">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <p className="font-bold text-ajisai-700">Master Data</p>
          <h1 className="mt-1 text-3xl font-black tracking-tight text-slate-950">{config.title}</h1>
          <p className="mt-2 text-sm text-slate-500">{config.description}</p>
        </div>
        {canWrite && (
          <button
            className="rounded-xl bg-ajisai-700 px-4 py-3 text-sm font-bold text-white shadow-lg shadow-ajisai-700/20"
            onClick={() => openForm(null)}
          >
            + Tambah {config.singular}
          </button>
        )}
      </div>
      <div className="mt-7 rounded-2xl border border-slate-200 bg-white shadow-sm">
        <form
          className="flex gap-3 border-b border-slate-200 p-4"
          onSubmit={(event) => {
            event.preventDefault();
            setPage(1);
            void load();
          }}
        >
          <input
            aria-label={`Cari ${config.title}`}
            className="min-w-0 flex-1 rounded-xl border border-slate-300 px-4 py-2.5 text-sm"
            placeholder={`Cari ${config.singular.toLowerCase()}…`}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <button className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-bold">
            Cari
          </button>
        </form>
        {error && <p className="m-4 rounded-xl bg-red-50 p-3 text-sm text-red-700">{error}</p>}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                {config.columns.map((column) => (
                  <th key={column.key} className="px-5 py-3">
                    {column.label}
                  </th>
                ))}
                {canWrite && <th className="px-5 py-3 text-right">Aksi</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {records.map((record) => (
                <tr key={record.id} className="hover:bg-slate-50/70">
                  {config.columns.map((column) => (
                    <td key={column.key} className="max-w-xs px-5 py-4 font-medium">
                      {column.key === 'name' && typeof record.imageUrl === 'string' ? (
                        <div className="flex items-center gap-3">
                          <img
                            className="size-10 rounded-lg object-cover"
                            src={record.imageUrl}
                            alt=""
                          />
                          {displayValue(record, column.key)}
                        </div>
                      ) : (
                        displayValue(record, column.key)
                      )}
                    </td>
                  ))}
                  {canWrite && (
                    <td className="whitespace-nowrap px-5 py-4 text-right">
                      <button
                        className="font-bold text-ajisai-700"
                        onClick={() => openForm(record)}
                      >
                        Edit
                      </button>
                      <button
                        className="ml-4 font-bold text-red-600"
                        onClick={() => {
                          void remove(record);
                        }}
                      >
                        Hapus
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          {!records.length && (
            <p className="p-12 text-center text-sm text-slate-500">Belum ada data.</p>
          )}
        </div>
        <div className="flex items-center justify-between border-t border-slate-200 px-5 py-4 text-sm">
          <p className="text-slate-500">{total} data</p>
          <div className="flex items-center gap-2">
            <button
              className="rounded-lg border px-3 py-2 disabled:opacity-40"
              disabled={page === 1}
              onClick={() => setPage((value) => value - 1)}
            >
              ←
            </button>
            <span className="font-bold">Halaman {page}</span>
            <button
              className="rounded-lg border px-3 py-2 disabled:opacity-40"
              disabled={page * limit >= total}
              onClick={() => setPage((value) => value + 1)}
            >
              →
            </button>
          </div>
        </div>
      </div>
      {editing !== undefined && (
        <div
          className="fixed inset-0 z-50 grid place-items-center bg-slate-950/50 p-4"
          role="dialog"
          aria-modal="true"
        >
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-bold text-ajisai-700">{editing ? 'Edit' : 'Tambah'}</p>
                <h2 className="text-2xl font-black">{config.singular}</h2>
              </div>
              <button
                aria-label="Tutup"
                className="text-xl text-slate-500"
                onClick={() => setEditing(undefined)}
              >
                ×
              </button>
            </div>
            <form
              className="mt-6 grid gap-5 sm:grid-cols-2"
              onSubmit={(event) => {
                void save(event);
              }}
            >
              {config.fields.map((field) => (
                <label
                  key={field.name}
                  className={`text-sm font-semibold text-slate-700 ${field.type === 'textarea' ? 'sm:col-span-2' : ''}`}
                >
                  {field.type === 'checkbox' ? (
                    <span className="flex items-center gap-3 rounded-xl border p-3">
                      <input
                        type="checkbox"
                        checked={Boolean(form[field.name])}
                        onChange={(event) =>
                          setForm({ ...form, [field.name]: event.target.checked })
                        }
                      />
                      {field.label}
                    </span>
                  ) : (
                    <>
                      {field.label}
                      {field.type === 'textarea' ? (
                        <textarea
                          className="mt-2 min-h-24 w-full rounded-xl border border-slate-300 px-4 py-3 font-normal"
                          required={field.required}
                          value={String(form[field.name] ?? '')}
                          onChange={(event) =>
                            setForm({ ...form, [field.name]: event.target.value })
                          }
                        />
                      ) : field.type === 'category' ? (
                        <select
                          className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 font-normal"
                          required
                          value={String(form[field.name] ?? '')}
                          onChange={(event) =>
                            setForm({ ...form, [field.name]: event.target.value })
                          }
                        >
                          <option value="">Pilih kategori</option>
                          {categories.map((category) => (
                            <option key={category.id} value={category.id}>
                              {scalar(category.name)}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 font-normal"
                          type={field.type ?? 'text'}
                          min={field.type === 'number' ? 0 : undefined}
                          required={field.required}
                          value={String(form[field.name] ?? '')}
                          onChange={(event) =>
                            setForm({ ...form, [field.name]: event.target.value })
                          }
                        />
                      )}
                    </>
                  )}
                </label>
              ))}
              {resource === 'products' && form.imageUrl && (
                <div className="sm:col-span-2">
                  <p className="mb-2 text-sm font-semibold">Preview gambar</p>
                  <img
                    className="h-40 w-full rounded-xl border object-cover"
                    src={String(form.imageUrl)}
                    alt="Preview produk"
                  />
                </div>
              )}
              {error && (
                <p className="sm:col-span-2 rounded-xl bg-red-50 p-3 text-sm text-red-700">
                  {error}
                </p>
              )}
              <div className="flex justify-end gap-3 sm:col-span-2">
                <button
                  type="button"
                  className="rounded-xl border px-4 py-3 font-bold"
                  onClick={() => setEditing(undefined)}
                >
                  Batal
                </button>
                <button
                  disabled={busy}
                  className="rounded-xl bg-ajisai-700 px-5 py-3 font-bold text-white disabled:opacity-60"
                >
                  {busy ? 'Menyimpan…' : 'Simpan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}
